import { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Share, StyleSheet } from 'react-native'

import { publishEvent } from '@zet/analytics'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import ResendWithTimer from '@zet/common/components/resendWithTimer'
import { useToast } from '@zet/common/components/toast'
import { useModal } from '@zet/common/hooks/useModal'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import {
  useSendConsentOtp,
  useShareReconsentOtpVerificationLink,
} from '@zet/customer/src/api/hooks/reconsent'
import useStore from '@zet/customer/src/store'
import { colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import { B1, B2, B5, H5, H6, SH2, Text } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import { OTP_REGEX } from '@zet/utils/regularExpression'
import LottieView from 'lottie-react-native'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'

import { IRequestData } from '../../api/functions/customerAdditionStateMachine'
import { useAddCustomerNextState } from '../../api/hooks'
import { useResendVerificationRequest } from '../../api/hooks/useResendVerificationRequest'
import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'

const { locale = 'en' } = zetApi.defaults.headers.common

const OtpLess = ({
  route,
}: {
  route: {
    params: {
      customerId: number | undefined
      isReconsentFlow: boolean
      isOtpLessLinkShared?: boolean
    }
  }
}) => {
  const {
    params: { customerId, isReconsentFlow = false, isOtpLessLinkShared = true },
  } = route
  const [value, setValue] = useState('')
  const [disableResend, setDisableResend] = useState(false)

  const ref = useBlurOnFulfill({ value, cellCount: 4 })
  const [isShared, setIsShared] = useState<boolean>(false)
  const [isOtpLessVerifyLinkShared, setIsOtpLessVerifyLinkShared] =
    useState<boolean>(isOtpLessLinkShared || false)

  const [isBottomButtonLoading, setIsBottomButtonLoading] = useState(false)
  const [isSentOnce, setIsSentOnce] = useState({
    otp: !isOtpLessLinkShared,
    verificationLink: isOtpLessLinkShared,
  })
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  })
  const { selectedCustomer } = useStore((state) => state)
  const [timer, setTimer] = useState<number>(30)
  const { openModal } = useModal()
  const navigation = useNavigation()
  const toast = useToast()
  const { mutate: sendConsentOtpMutate } = useSendConsentOtp()
  const { data: user } = useUserContext()
  const { mutate: mutateWithExperiments, isLoading: isExperimentsLoading } =
    useAddCustomerNextState()
  const BaseRequestBodyData = {
    customerId: customerId as number,
    otp: isOtpLessVerifyLinkShared ? '' : value,
    phoneNumber: selectedCustomer?.phoneNumber as string,
  }

  const handleButtonDisablity = () => {
    if (isShared) return false
    else if (value.length === 4 && OTP_REGEX.test(value)) return false
    else return true
  }

  const onReconsentShare = async (response) => {
    const result = await Share.share({
      message: i18n.t('otpInput.shareReconsentDescription', {
        link: response?.data?.link,
        name: user?.fullName,
      }),
    })
    setIsShared(true)
  }

  const { mutate: mutateResendVerificationRequest } =
    useResendVerificationRequest()

  const {
    refetch: refetchReconsent,
    isLoading: refetchingReconsent,
    isFetching: isFetchingReconsent,
  } = useShareReconsentOtpVerificationLink({
    onSuccess: onReconsentShare,
    customerId,
  })

  //Timer
  const interval = 1000
  const timerRef = useRef(0)
  let prev = 0
  let diff = 0
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }
  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  useEffect(() => {
    otpTimer()
    if (isReconsentFlow) {
      publishEvent('screen_view', {
        screen_name: 'brands_list_recommendation_get_customer_consent',
      })
    }
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  useEffect(() => {
    //call timer of resend link if you come through the case where link is shared first
    // call timer of resend link if you press on resend link
    //call timer of resend otp if you come through the case where otp is shared first
    // call timer of resend otp if you press on resend otp
  }, [])

  const handleConsentSubmit = () => {
    const requestBodyData = {
      ...BaseRequestBodyData,
      screenName: 'SHOWN_OTP_SCREEN',
      isReconsentFlow,
    }
    mutateWithExperiments(requestBodyData as IRequestData)
  }

  const sendVerificationLinkToCustomer = () => {
    const requestBodyData = {
      ...BaseRequestBodyData,
      fullName: selectedCustomer?.name,
      phoneNumber: selectedCustomer?.phoneNumber,
      screenName: 'SHOWN_OTP_SCREEN',
      event: 'ASK_OTP_LESS_VERIFICATION',
      triggerSend: !isSentOnce.verificationLink,
      isReconsentFlow,
    }
    mutateWithExperiments(requestBodyData as IRequestData, {
      onSuccess() {
        if (!isSentOnce?.verificationLink) {
          setIsSentOnce({ ...isSentOnce, verificationLink: true })
        }
        setIsOtpLessVerifyLinkShared(true)
      },
    })
  }

  const sendOtpToCustomer = () => {
    const requestBodyData = {
      ...BaseRequestBodyData,
      fullName: selectedCustomer?.name,
      phoneNumber: selectedCustomer?.phoneNumber,
      screenName: 'SHOWN_OTP_LESS_SCREEN',
      event: 'ASK_OTP_VERIFICATION',
      triggerSend: !isSentOnce.otp,
      isReconsentFlow,
    }

    mutateWithExperiments(requestBodyData as IRequestData, {
      onSuccess() {
        if (!isSentOnce?.otp) {
          setIsSentOnce({ ...isSentOnce, otp: true })
        }
        setIsOtpLessVerifyLinkShared(false)
      },
    })
  }

  const resendVerifyLink = (isOtpLess: boolean) => {
    mutateResendVerificationRequest(
      {
        fullName: selectedCustomer?.name,
        phoneNumber: selectedCustomer?.phoneNumber,
        otpLess: isOtpLess,
      },
      {
        onSuccess: (data) => {
          if (data.lockedTill) {
            setDisableResend(true)
          }
        },
      }
    )
  }

  const onPressBottomButton = (screenName: string) => {
    const requestBodyData = {
      ...BaseRequestBodyData,
      screenName,
      isReconsentFlow,
    }
    setIsBottomButtonLoading(true)
    mutateWithExperiments(requestBodyData as IRequestData, {
      onSettled(data) {
        if (data?.actionData?.error?.code === 3313) {
          toast?.show({
            intent: 'error',
            heading: data?.actionData?.error?.message,
            hideAfter: 3000,
          })
        }
        setIsBottomButtonLoading(false)
      },
    })
  }
  const getSubmitButtonDetails = () => {
    if (!isOtpLessVerifyLinkShared) {
      return {
        onPress: () => onPressBottomButton('SHOWN_OTP_SCREEN'),
        buttonName: i18n.t('otpInput.verifyOtp'),
        loaderText: i18n.t('otpInput.verifyingOtp'),
        isDisabled: handleButtonDisablity(),
        eventItemName: 'add_customer_step_1_continue',
      }
    }
    if (isOtpLessVerifyLinkShared) {
      return {
        onPress: () => onPressBottomButton('SHOWN_OTP_LESS_SCREEN'),
        buttonName: i18n.t('customerDetails.permissionStatus'),
        loaderText: i18n.t('customerDetails.permissionStatusLoading'),
        isDisabled: false,
        eventItemName: 'add_customer_step_1_continue_otp_less',
      }
    }
  }

  const openYoutubeModal = () =>
    openModal('YoutubeVideoModal', {
      videoId: 'SGfT09xM1k0',
      videoData: {
        en: { videoId: 'SGfT09xM1k0' },
        hi: { videoId: 'KOtS5Qd0kYY' },
      },
    })

  return (
    <LazyHOC>
      <AddCustomerBackHandlerHOC>
        <KeyBoardWrapper
          className="flex-1 bg-white "
          BottomSheet={
            <>
              {isReconsentFlow ? (
                <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
                  <Button
                    className="w-full rounded py-[12]"
                    onPress={getSubmitButtonDetails()?.onPress}
                    disabled={getSubmitButtonDetails()?.isDisabled}
                    loading={isBottomButtonLoading}
                    eventName="click_action"
                    eventPayload={{
                      item_name: 'add_customer_reconsent_verify_otp_continue',
                    }}
                  >
                    {i18n.t('reconsent.continue')}
                  </Button>
                </Stack>
              ) : (
                <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
                  <Button
                    className="w-full rounded py-[12]"
                    onPress={getSubmitButtonDetails()?.onPress}
                    disabled={getSubmitButtonDetails()?.isDisabled}
                    loading={isBottomButtonLoading}
                    eventName="click_action"
                    eventPayload={{
                      item_name: getSubmitButtonDetails()?.eventItemName,
                    }}
                    loaderText={getSubmitButtonDetails()?.loaderText}
                  >
                    {getSubmitButtonDetails()?.buttonName}
                  </Button>
                </Stack>
              )}
            </>
          }
        >
          <Stack className="px-3">
            <Stack className="h-3" />
            <H5 className="text-grays-100">
              {isReconsentFlow
                ? i18n.t('customerDetails.consentHeader')
                : i18n.t('customerDetails.customerPermission')}
            </H5>

            {isOtpLessVerifyLinkShared ? (
              <>
                <Stack className=" mt-9 ">
                  <LottieView
                    autoPlay
                    className=" h-[192] w-[192] self-center "
                    source={require('../../../assets/lottie/mobile-verify-link.json')}
                  />
                </Stack>
                <ResendWithTimer
                  disabled={disableResend}
                  onPress={() => {
                    prev = 0
                    diff = 0
                    timerFunction()
                    if (isReconsentFlow) {
                      return
                    } else {
                      resendVerifyLink(true)
                    }
                  }}
                  onPressEventPayload={{
                    item_name: isReconsentFlow
                      ? 'brands_list_recommendation_reconsent_resend_otp'
                      : 'add_customer_resend_otp',
                  }}
                  timer={timer}
                  timerOffText={i18n.t('otpInput.resendLink')}
                  timerOnText={i18n.t('otpInput.resendLinkIn')}
                />
                <B1 className="text-grays-60 mt-20 ">
                  {i18n.t('customerDetails.verificationLinkSent')}

                  <H6 className=" text-grays-100 border">
                    {' '}
                    {selectedCustomer?.phoneNumber}.{' '}
                  </H6>

                  {i18n.t('customerDetails.askToGiveConsent')}
                </B1>
              </>
            ) : (
              <>
                <B1 className=" text-grays-60 mt-2">
                  {i18n.t('customerDetails.sentOtp')}{' '}
                  <H6 className=" text-grays-100 border">
                    {selectedCustomer?.phoneNumber}.{' '}
                  </H6>
                  {i18n.t('customerDetails.fillOtp')}
                </B1>
                <Stack>
                  <CodeField
                    ref={ref}
                    {...props}
                    caretHidden={true}
                    value={value}
                    onChangeText={(i) => {
                      setValue(i)
                    }}
                    cellCount={4}
                    rootStyle={styles.codeFieldRoot}
                    keyboardType="number-pad"
                    textContentType="oneTimeCode"
                    renderCell={({ index, symbol, isFocused }) => (
                      <Text
                        key={index}
                        style={[styles.cell, isFocused && styles.focusCell]}
                        onLayout={getCellOnLayoutHandler(index)}
                      >
                        {symbol || (isFocused ? <Cursor /> : null)}
                      </Text>
                    )}
                  />
                  {!OTP_REGEX.test(value) && (
                    <B2 className="text-error-100 mt-2 self-center">
                      {i18n.t('otpInput.invalidOtp')}
                    </B2>
                  )}
                </Stack>
                <ResendWithTimer
                  disabled={disableResend}
                  onPress={() => {
                    prev = 0
                    diff = 0
                    timerFunction()
                    if (isReconsentFlow) {
                      sendConsentOtpMutate({
                        fullName: selectedCustomer?.name,
                        phoneNumber: selectedCustomer?.phoneNumber,
                      })
                    } else {
                      resendVerifyLink(false)
                    }
                  }}
                  onPressEventPayload={{
                    item_name: isReconsentFlow
                      ? 'brands_list_recommendation_reconsent_resend_otp'
                      : 'add_customer_resend_otp',
                  }}
                  timer={timer}
                  timerOffText={i18n.t('otpInput.resend')}
                  timerOnText={i18n.t('otpInput.resendOtpIn')}
                />
                {APP_NAME !== MY_TEAMS_ADVISOR && (
                  <Stack className=" mt-5 rounded-lg bg-[#ECFFEC] pb-2  ">
                    <Alert
                      intent="success"
                      iconLeft={{
                        name: 'SecureOutlined',
                        color: zetColors.success[150],
                      }}
                      className=" text-grays-60 "
                    >
                      {i18n.t('customerDetails.whyOtpRequired')}
                    </Alert>
                    <Button
                      onPress={openYoutubeModal}
                      iconLeft={<Icon type="VideoOutlined" size={16} />}
                      intent="primary"
                      outlined={true}
                      className="border-grays-60 mx-0  ml-[38] h-4 w-[120] rounded-xl px-1 py-[1.5]"
                      textStyle=" text-xs font-[Graphie-SemiBold] text-grays-60  "
                      eventName="click_action"
                      eventPayload={{
                        item_name: 'add_customer_verify_otp_watch_video',
                      }}
                    >
                      {i18n.t('customerDetails.watch')}
                    </Button>
                  </Stack>
                )}

                <B2 className=" mt-5 ">
                  {i18n.t('verifyPhNum.agree')}
                  <SH2
                    onPress={() => {
                      navigation.navigate('WebViewScreen', {
                        url: 'https://onecode-asset.s3.ap-south-1.amazonaws.com/Web+site-T%26C-Policies/OneCodersEULA-OneCode.html',
                      })
                      publishEvent('click_action', {
                        item_name: 'add_customer_terms_of_use',
                      })
                    }}
                  >
                    {' '}
                    {i18n.t('verifyPhNum.terms')}
                  </SH2>
                  <B2>{i18n.t('verifyPhNum.and')}</B2>
                  <SH2
                    onPress={() => {
                      navigation.navigate('WebViewScreen', {
                        url: 'https://onecode-asset.s3.ap-south-1.amazonaws.com/Web+site-T%26C-Policies/OneCodersPrivacyPolicy-OneCode.html',
                      })
                      publishEvent('click_action', {
                        item_name: 'add_customer_privacy_policy',
                      })
                    }}
                  >
                    {' '}
                    {i18n.t('verifyPhNum.privacyPolicy')}.
                  </SH2>
                  <B2>
                    {i18n.t('verifyPhNum.agreeSuffix', {
                      continue:
                        locale === 'hi' ? i18n.t('verifyPhNum.continue') : '',
                    })}
                  </B2>
                </B2>
              </>
            )}
            <Stack className="   mb-4 justify-end self-center ">
              <XStack className="my-[8%] w-full items-center justify-center  ">
                <Stack className="bg-grays-30 h-[1]   flex-grow"></Stack>
                <B5 className="border-grays-30 rounded-[32] border px-2 py-1">
                  {i18n.t('knowCustomer.or')}
                </B5>
                <Stack className="bg-grays-30  h-[1] flex-grow"></Stack>
              </XStack>
              {isOtpLessVerifyLinkShared ? (
                <Button
                  textOnly={true}
                  className="w-full self-center"
                  iconLeft={
                    <Icon
                      type="MessageFilled"
                      color={zetColors.primary[50]}
                      className=" mr-2 "
                    />
                  }
                  textStyle="text-primary-100"
                  onPress={sendOtpToCustomer}
                  loading={isExperimentsLoading}
                  eventName="click_action"
                  eventPayload={{
                    item_name: isReconsentFlow
                      ? 'brands_list_recommendation_reconsent_enter_otp_manually'
                      : 'add_customer_otp_manually_otp_less',
                  }}
                >
                  {i18n.t('otpInput.askOtpFromCustomer')}
                </Button>
              ) : (
                <Button
                  textOnly={true}
                  className="w-full self-center"
                  iconLeft={
                    isExperimentsLoading ||
                    (refetchingReconsent && isFetchingReconsent) ? (
                      <ActivityIndicator
                        size="small"
                        color={zetColors.primary[100]}
                      />
                    ) : (
                      <Icon
                        type="Share"
                        color={zetColors.primary[50]}
                        className=" mr-2 "
                      />
                    )
                  }
                  textStyle="text-primary-100"
                  onPress={() =>
                    isReconsentFlow
                      ? refetchReconsent()
                      : sendVerificationLinkToCustomer()
                  }
                  eventName="click_action"
                  eventPayload={{
                    item_name: isReconsentFlow
                      ? 'brands_list_recommendation_reconsent_share_otp'
                      : 'add_customer_share_otp_less_verification_link',
                  }}
                >
                  {i18n.t('customerDetails.shareOtpLess')}
                </Button>
              )}
            </Stack>
          </Stack>
        </KeyBoardWrapper>
      </AddCustomerBackHandlerHOC>
    </LazyHOC>
  )
}
const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 44,
    marginHorizontal: 55,
  },
  cell: {
    width: 52,
    height: 48,
    lineHeight: 47,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 1,
    borderColor: zetColors.grays[40],
    textAlign: 'center',
    borderRadius: 6,
    paddingHorizontal: 0,
    backgroundColor: '#F9F9FB',
  },
  focusCell: {
    borderColor: zetColors.primary[50],
  },
})
export default OtpLess
